<template>
  <div :class="notificationClass(notify)"
       role="alert" :aria-labelledby="id + '-title'" :id="id" v-for="[id, notify] in notificationsComputed">
    <section aria-label="title" :id="id + '-title'" class="h5">
      <svg v-if="notify.status || notify.icon" class="icon">
        <use :href="'/dist/svg/sprites.svg#' + notificationIcon(notify)"></use>
      </svg>
      {{ notify.title }}
    </section>
    <section aria-label="message">
      <p class="text-black text-wrap" v-if="notify.message" v-html="DOMPurify.sanitize(notify.message ?? '')"/>
    </section>
    <button
        v-if="notify.dismissable || notify.status === NotificationStatus.ERROR || notify.status === NotificationStatus.WARNING"
        type="button" class="btn notification-close" @click="hide(id)"
        data-bs-toggle="tooltip" title="Chiudi notifica">
      <svg class="icon">
        <use href="/dist/svg/sprites.svg#it-close"></use>
      </svg>
      <span role="banner" class="visually-hidden text-wrap">Chiudi notifica: {{ notify.title }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">

import DOMPurify from 'dompurify'
import {INotification} from "@/interfaces/components/general/INotification";
// @ts-ignore
import {Notification} from 'bootstrap-italia'
import {ref, Ref, watch} from "vue";
import {NotificationStatus} from "@/enums/components/general/NotificationStatus";
import {useNotificationStore} from "@/stores/notification";

const notificationStore = useNotificationStore();
const notifications = ref<Map<string, boolean>>(new Map<string, boolean>())
const notificationsElement = ref<Map<string, Notification>>(new Map<string, Notification>())
const notificationsComputed = notificationStore.notificationsData as Ref<Map<string, INotification>>

function notificationIcon(notify: INotification) {
  if (notify.icon) {
    return notify.icon
  }
  switch (notify.status) {
    case NotificationStatus.INFO:
      return 'it-info-circle';
    case NotificationStatus.SUCCESS:
      return 'it-check-circle'
    case NotificationStatus.ERROR:
      return 'it-close-circle'
    case NotificationStatus.WARNING:
      return 'it-error'
    default:
      return undefined
  }
}

function notificationClass(notify: INotification) {
  const cssClass = ['notification', 'topLayerNotification'];
  if (notify.icon || notify.status) {
    cssClass.push('with-icon');
  }
  if (notify.status) {
    cssClass.push(notify.status)
  }
  if (notify.position) {
    cssClass.push(notify.position)
  }
  if (notify.status === NotificationStatus.ERROR || notify.status === NotificationStatus.WARNING) {
    notify.dismissable = true
    notify.timeout = 10000
    cssClass.push('dismissable')
  }
  return cssClass.join(' ');
}

watch(notificationsComputed, (value: Map<string, INotification>) => {
  const newKeys = Array.from(value.keys());
  newKeys.forEach((key: string) => {
    if (!notifications.value.has(key)) {
      notifications.value.set(key, false);
    }
  })
}, {deep: true})

watch(notifications, (value: Map<string, boolean>) => {
  value.forEach((completed: boolean, key: string) => {
    runProcess(completed, key)
  })
}, {deep: true})

//TODO: Trovare una soluzione alternativa al timeout
setInterval(function () {
  notifications.value.forEach((completed: boolean, key: string) => {
    runProcess(completed, key)
  })
}, 1000);

function runProcess(completed: boolean, key: string) {
  if (!completed) {
    const element = document.getElementById(key);
    if (element) {
      notifications.value.set(key, true);
      if (!notificationsElement.value.has(key)) {
        const notifica: INotification | undefined = notificationsComputed.value.get(key);
        if (notifica !== undefined) {
          const timeout: number = notifica.timeout ?? 6000;
          const notificationElement = new Notification(element, {timeout: timeout});
          notificationsElement.value.set(key, notificationElement)
          notificationElement.show();
          // È stato gestito in questo modo perché purtroppo non funzionano gli event listener di bootstrap italia
          setTimeout(() => {
            hide(key);
          }, timeout + 300)
        }
      }
    }
  }
}

function hide(id: string) {
  const notificationElement: Notification | undefined = notificationsElement.value.get(id);
  const notifica: INotification | undefined = notificationsComputed.value.get(id);
  if (notificationElement !== undefined) {
    notificationElement.hide();
    notifications.value.delete(id);
    notificationStore.removeNotification(id);
    const element = document.getElementById(id);
    if (element) {
      element.innerHTML = '';
    }
    notifica?.afterDismissCallback?.()
  }
}

</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.topLayerNotification {
  z-index: 100000000;
}

.notification {
  .notification-close {
    position: absolute;
    right: 0;
    top: 0;
    display: block;
    width: 44px;
    height: 44px;
    padding: 0;
    background: none;
  }

}
</style>
